import React from "react";

const useCases = [
  {
    title: "Enterprise RAG Pipeline Migration",
    desc: "Production RAG systems require consistent, high-performance vector retrieval to maintain response quality and user experience. Vector database migration enables RAG pipelines to scale from prototype to enterprise deployment.",
    scenario: "Migrating from Chroma (development) to Pinecone or Qdrant (production) as RAG applications mature.",
    points: [
      "Reduce P95 query latency for better user experience",
      "Scale to millions of documents without degradation",
      "Enable metadata filtering for context-aware retrieval",
      "Support multi-tenant AI applications",
    ],
  },
  {
    title: "Multi-Model LLM Application Infrastructure",
    desc: "Modern AI systems leverage multiple embedding models and LLMs. Vector database migration allows teams to evolve retrieval infrastructure without regenerating embeddings.",
    scenario: "Unifying multiple collections across different AI use cases into a single platform.",
    points: [
      "Support multiple embedding dimensions",
      "Switch embedding models without full reindexing",
      "Enable A/B testing for retrieval quality",
      "Combine multiple vector databases for specialisation",
    ],
  },
  {
    title: "Semantic Search & Recommendation Engines",
    desc: "High-traffic semantic search systems demand ultra-low latency and precise similarity matching. Migration enables optimisation for recall, ranking, and scalability.",
    scenario: "E-commerce platforms improving relevance and CTR through optimised vector search.",
    points: [
      "Optimise indexing strategies for recall accuracy",
      "Support real-time updates for dynamic catalogs",
      "Enable hybrid keyword + semantic search",
      "Reduce latency for global users",
    ],
  },
  {
    title: "Avoiding Vendor Lock-In & Data Sovereignty",
    desc: "Enterprises require flexibility to change infrastructure providers due to cost, compliance, or performance needs. Migration ensures full control over vector data.",
    scenario: "Financial and healthcare organisations migrating to self-hosted deployments.",
    points: [
      "Maintain ownership of embedding data",
      "Meet regulatory and data residency requirements",
      "Adopt multi-cloud strategies",
      "Stay future-proof as vector tech evolves",
    ],
  },
];

const additionalScenarios = [
  {
    title: "Disaster Recovery & High Availability",
    text: "Maintain synchronised vector databases across regions to ensure business continuity for AI systems.",
  },
  {
    title: "Development to Production Workflows",
    text: "Move seamlessly from lightweight dev databases to production-grade vector platforms.",
  },
  {
    title: "Cost Reduction Initiatives",
    text: "Consolidate subscriptions or migrate to self-hosted solutions to reduce operational costs.",
  },
  {
    title: "Legacy System Modernisation",
    text: "Transition from traditional search systems to modern vector databases for AI workloads.",
  },
];

export default function MigrationUseCases() {
  return (
    <section id="use-cases" className="scroll-mt-20">

      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-12 bg-white rounded-full" />
        <h2 className="text-3xl md:text-4xl font-bold text-white">
          AI & RAG Use Cases for Vector Database Migration
        </h2>
      </div>

      <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10">
        Real-world applications demonstrate how vector database migration enables advanced AI
        capabilities, improves system reliability, and supports business-critical operations
        across industries.
      </p>

      {useCases.map((item, idx) => (
        <div
          key={idx}
          className="mb-10 border border-primary-soft rounded-2xl p-6 md:p-8 bg-primary hover:border-white/40 transition-colors"
        >
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">{item.title}</h3>
            <p className="text-white/70 leading-relaxed mb-4 text-sm md:text-base">{item.desc}</p>
            <ul className="space-y-2 text-white/70 mb-4 text-sm md:text-base">
              {item.points.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-bold text-white">→</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-white/50">
              <strong className="text-white/80">Common scenario:</strong> {item.scenario}
            </p>
          </div>
        </div>
      ))}

      <div className="mt-14">
        <h3 className="text-2xl font-semibold mb-6 text-white">
          Additional Vector Database Migration Scenarios
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {additionalScenarios.map((card, i) => (
            <div
              key={i}
              className="border border-primary-soft rounded-xl p-6 bg-primary-dark hover:border-white/40 transition-colors"
            >
              <h4 className="font-semibold text-lg mb-2 text-white">{card.title}</h4>
              <p className="text-white/60 text-sm leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}